import z from "zod";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { QueryClient } from "@tanstack/react-query";

import { audienceExportsQueryKey } from "@/api/audiences";
import { fetchExportJob } from "@/api/listsFeatureV2";

const trackedExportSchema = z.object({
  siteId: z.string(),
  jobId: z.string(),
  name: z.string(),
  kind: z.enum(["list", "audience"]),
  status: z.enum(["running", "ready", "failed"]),
  fileUrl: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type TrackedExport = z.infer<typeof trackedExportSchema>;
export type ExportKind = TrackedExport["kind"];

interface TrackedExportsState {
  items: TrackedExport[];
}

export const EXPORT_POLL_INTERVAL_MS = 5000;
export const MAX_POLL_ERRORS = 3;

export const useTrackedExportsStore = create<TrackedExportsState>()(
  persist(() => ({ items: [] }), {
    name: "trackedExports",
    storage: createJSONStorage(() => sessionStorage),
    merge: (persisted, current) => {
      const parsed = z
        .object({ items: z.array(trackedExportSchema) })
        .safeParse(persisted);
      return parsed.success ? { ...current, ...parsed.data } : current;
    },
  }),
);

const getItems = () => useTrackedExportsStore.getState().items;
const setItems = (items: TrackedExport[]) =>
  useTrackedExportsStore.setState({ items });

class ExportTracker {
  private qc: QueryClient | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private errorCounts = new Map<string, number>();

  init(queryClient: QueryClient) {
    this.qc = queryClient;
    this.schedule();
  }

  track(item: Omit<TrackedExport, "status" | "fileUrl" | "updatedAt">) {
    const rest = getItems().filter((i) => i.jobId !== item.jobId);
    setItems([...rest, { ...item, status: "running" }]);
    this.invalidateListing(item.siteId);
    this.schedule();
  }

  dismiss(jobId: string) {
    this.errorCounts.delete(jobId);
    setItems(getItems().filter((i) => i.jobId !== jobId));
  }

  clear() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.errorCounts.clear();
    setItems([]);
  }

  private schedule() {
    if (this.timer || !getItems().some((i) => i.status === "running")) {
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.pollAll();
    }, EXPORT_POLL_INTERVAL_MS);
  }

  private async pollAll() {
    const running = getItems().filter((i) => i.status === "running");
    await Promise.all(running.map((item) => this.pollOne(item)));
    this.schedule();
  }

  private async pollOne(item: TrackedExport) {
    try {
      const { job } = await fetchExportJob(item.siteId, item.jobId);
      this.errorCounts.delete(item.jobId);
      if (job.status === "JOB_STATUS_COMPLETED") {
        this.settle(item, {
          status: "ready",
          fileUrl: job.result?.fileUrl,
          updatedAt: job.updatedAt,
        });
      } else if (
        job.status === "JOB_STATUS_FAILED" ||
        job.status === "JOB_STATUS_CANCELLED"
      ) {
        this.settle(item, { status: "failed" });
      }
    } catch {
      const count = (this.errorCounts.get(item.jobId) ?? 0) + 1;
      this.errorCounts.set(item.jobId, count);
      if (count >= MAX_POLL_ERRORS) {
        this.settle(item, { status: "failed" });
      }
    }
  }

  private settle(item: TrackedExport, patch: Partial<TrackedExport>) {
    this.errorCounts.delete(item.jobId);
    const items = getItems();
    if (items.some((i) => i.jobId === item.jobId)) {
      setItems(
        items.map((i) => (i.jobId === item.jobId ? { ...i, ...patch } : i)),
      );
    }
    this.invalidateListing(item.siteId);
  }

  private invalidateListing(siteId: string) {
    void this.qc?.invalidateQueries({
      queryKey: audienceExportsQueryKey(siteId),
    });
  }
}

export const exportTracker = new ExportTracker();
